import { Inject, Injectable } from '@angular/core';
import { BaseCrudService } from '@t-llevo/common-ui';
import { Environment, ResponseData } from '@t-llevo/util';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { apiUserRoutes as apiRoutes } from '../routes/api-user-routes';
import { Observable, filter, forkJoin, map, switchMap } from 'rxjs';
import { UserResponse } from '../models/responses/UserResponse';
import { UserRequest } from '../models/requests/UserRequest';
import { ChangePasswordRequest } from '../models/requests/ChangePasswordRequest';
import { UserMapper } from '../mappers/UserMapper';
import { User } from '../../common/models/User';

@Injectable()
export class UserService extends BaseCrudService<User, ResponseData<UserResponse>> {
  constructor(@Inject(Environment) env: Environment, http: HttpClient) {
    super(env, http);
  }

  override transform(data: ResponseData<UserResponse>): ResponseData<User> {
    return {
      ...data,
      results: data.results.map((user) => UserMapper(user)),
    };
  }

  createUser(params: UserRequest) {
    const url = this.env.apiUrl + apiRoutes.routeRegisterUser;
    return this.http.post<UserResponse>(url, params);
  }

  updateUserById(params: Omit<UserRequest, 'password' | 'roles'>, id: string) {
    const url = this.env.apiUrl + apiRoutes.routeEditUser + id;
    return this.http
      .patch<UserResponse>(url, params)
      .pipe(switchMap(() => this.getUserById(id)));
  }

  deleteUserById(id: string) {
    const url = this.env.apiUrl + apiRoutes.routeDeleteUser + id;
    return this.http.delete(url);
  }

  getUsers(params?: object) {
    const url = this.env.apiUrl + apiRoutes.routeGetClientList;
    return this.http.get<ResponseData<UserResponse>>(url, { params: { ...params } })
    .pipe(map(data => {
      return {
        ...data,
        results: data.results.map(user => UserMapper(user))
      }
    }))
  }

  getUserById(id: string) {
    const url = this.env.apiUrl + apiRoutes.routeGetUser + id;
    return this.http.get<UserResponse>(url);
  }

  changePassword(data: ChangePasswordRequest) {
    const url = this.env.apiUrl + apiRoutes.routeChangePassword;
    return this.http.post(url, data);
  }

  private checkIfEmailExist(email: string) {
    const url = this.env.apiUrl + apiRoutes.routeEmailExist;
    return this.http.post(url, { email });
  }

  private checkIfPhoneExist(phone: string) {
    const url = this.env.apiUrl + apiRoutes.routePhoneExist;
    return this.http.post(url, { phone });
  }

  emailAndPhoneValidator(email: string, phone: string) {
    return forkJoin({
      existEmail: this.checkIfEmailExist(email),
      existPhone: this.checkIfPhoneExist(phone),
    }).pipe(
      map((data) => {
        if (data.existEmail && data.existPhone) {
          throw new HttpErrorResponse({
            error: {
              message:
                'El número telefónico y el correo electrónico están registrados',
            },
          });
        } else if (data.existEmail) {
          throw new HttpErrorResponse({
            error: { message: 'El correo electrónico está registrado' },
          });
        } else if (data.existPhone) {
          throw new HttpErrorResponse({
            error: { message: 'El número telefónico está registrado' },
          });
        }

        return data;
      }),
    );
  }

  getUserByEmailOrPhoneNumber(email: string, phoneNumber: string) {
    return this.getUsers().pipe(
      map((data) => {
        return data.results.find(
          (item) => item.email == email || item.phone == phoneNumber,
        );
      }),
    );
  }

  userHasRole(id: string, role: string) {
    return this.getUserById(id).pipe(map((user) => user.roles.includes(role)));
  }
}
